/**
 * @file `useBudget` — TanStack-backed read of the learner's daily
 *       VM-time budget for the chrome meter (#941).
 * @module @labcraft/lab/ui/hooks/use-budget
 *
 * The budget meter reads this once per chrome mount and shares the
 * `qkBudget` cache entry across readers. `tier` gates the meter's
 * presence (paid → no meter); the `usedMsToday` / `limitMs` pair drives
 * the remaining-time ring; `upgradeUrl` is the shared "go unlimited"
 * target. The query is only mounted when a learner is present (the app
 * bar gates `<BudgetMeter>` on `useMe`), so the `/me/budget` 401 path
 * never fires for an anonymous request.
 *
 * Budget moves slowly (a UTC-day counter) and the SPA tolerates mild
 * staleness — the workspace's 30s `staleTime` plus a refetch on the
 * next mount is enough; the authoritative wall is the WS
 * `budget-exhausted` frame, not this poll.
 *
 * While the VM is actively running, the meter ticks a live countdown by
 * extrapolating from the last snapshot (#1315). To keep that
 * extrapolation honest, callers pass `reconcileWhileRunning` so the
 * query polls on {@link BUDGET_RECONCILE_MS} and re-baselines the
 * countdown against the server's authoritative `usedMsToday`; the poll
 * is off (mount + focus only) whenever no budget is being spent.
 *
 * Convention: hooks live in this directory and never call `useQuery`
 * outside it. Components import workspace hooks (`useBudget`, …) only.
 */

import { useQuery } from '@tanstack/react-query'
import { useClient } from '@labcraft/core/frontend/react'
import type { Lab } from '../../client/index.ts'
import type { BudgetResponse } from '../../shared/account_routes.ts'
import { qkBudget } from './query_keys.ts'

/** Reconcile cadence — refetch the snapshot this often while the VM runs. */
const BUDGET_RECONCILE_MS = 30_000

/**
 * Subscribe to the current learner's daily VM-time budget from React.
 * The first reader drives the fetch; subsequent readers share the
 * cache. Returns the full TanStack result — callers read `data?.tier`
 * and supply their own loading fallback.
 *
 * @param reconcileWhileRunning - When `true`, poll every
 *   {@link BUDGET_RECONCILE_MS} so the live countdown re-baselines to
 *   the server snapshot; pass the meter's `vmRunning`. Off by default.
 * @returns The TanStack query result for the budget snapshot.
 */
export function useBudget(reconcileWhileRunning = false) {
  const client = useClient<Lab>()
  return useQuery<BudgetResponse>({
    queryKey: qkBudget,
    queryFn: () => client.account.budget(),
    refetchInterval: reconcileWhileRunning ? BUDGET_RECONCILE_MS : false,
  })
}
